import { Card, Badge, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * Shared shell for a "needs review" request card. Owns the chrome common to
 * both the community-creation queue and the moderator-recruitment queue: the
 * card wrapper, optional hero image, the "Needs review" kicker with category
 * badge, and the approve/reject action column. The queue-specific body (name,
 * description, guidelines, or the recruitment one-liner) is passed as children
 * and rendered between the kicker and the actions.
 * @param {object} props
 * @param {object} props.community - Community being reviewed (reads heroImage, category)
 * @param {boolean} props.isBusy - Disables the action buttons while a decision is in flight
 * @param {Function} props.onApprove - Click handler for the approve button
 * @param {Function} props.onReject - Click handler for the reject button
 * @param {string} props.approveLabel - Accessible label for the approve button
 * @param {string} props.rejectLabel - Accessible label for the reject button
 * @param {React.ReactNode} props.children - Queue-specific card body
 */
export default function RequestReviewCard({
    community,
    isBusy,
    onApprove,
    onReject,
    approveLabel,
    rejectLabel,
    children,
}) {
    return (
        <Card className="pending-review-card shadow-sm overflow-hidden rounded-3">
            {community.heroImage && (
                <img
                    src={community.heroImage}
                    alt=""
                    className="pending-review-hero"
                />
            )}
            <Card.Body className="pending-review-card-body d-grid">
                <div className="pending-review-main">
                    <div className="pending-review-kicker d-flex flex-wrap align-items-center gap-2 justify-content-md-between">
                        <span className="pending-review-status d-inline-flex align-items-center fw-bold text-uppercase">
                            <i
                                className="bi bi-hourglass-split"
                                aria-hidden="true"
                            />
                            Needs review
                        </span>
                        <Badge
                            bg="light"
                            text="dark"
                            className="border fw-semibold"
                        >
                            {community.category}
                        </Badge>
                    </div>

                    {children}
                </div>

                {/* Action column stacks vertically; additional buttons
                    (e.g., "Request Changes") can be added here without
                    restructuring the layout. */}
                <div className="pending-review-actions d-grid d-md-flex flex-md-column align-items-md-center justify-content-md-start gap-2">
                    <Button
                        variant="success"
                        disabled={isBusy}
                        onClick={onApprove}
                        aria-label={approveLabel}
                    >
                        <i
                            className="bi bi-check2-circle me-2"
                            aria-hidden="true"
                        />
                        Approve
                    </Button>
                    <Button
                        variant="outline-danger"
                        disabled={isBusy}
                        onClick={onReject}
                        aria-label={rejectLabel}
                    >
                        <i className="bi bi-x-circle me-2" aria-hidden="true" />
                        Reject
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
}

RequestReviewCard.propTypes = {
    community: PropTypes.shape({
        heroImage: PropTypes.string,
        category: PropTypes.string,
    }).isRequired,
    isBusy: PropTypes.bool,
    onApprove: PropTypes.func.isRequired,
    onReject: PropTypes.func.isRequired,
    approveLabel: PropTypes.string,
    rejectLabel: PropTypes.string,
    children: PropTypes.node,
};
